import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  CardContent,
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Chip,
  Button,
  Skeleton,
} from '@mui/material';
import {
  Visibility as ViewIcon,
  ArrowForward as ArrowIcon,
  FiberManualRecord as DotIcon,
} from '@mui/icons-material';
import { trackingApi } from '../../services/api';
import { formatRelativeDate, formatInstagramHandle } from '../../utils/helpers';

const ActiveLeadsCard = () => {
  const navigate = useNavigate();
  const [activeLeads, setActiveLeads] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchActiveLeads = async () => {
      try {
        const response = await trackingApi.getActiveLeads({ days: 7, limit: 8 });
        setActiveLeads(response.data.activeLeads || []);
      } catch (error) {
        console.error('Failed to fetch active leads:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchActiveLeads();
  }, []);

  return (
    <Card sx={{ height: '100%' }}>
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="subtitle2" fontWeight={600}>
              Active Leads
            </Typography>
            {activeLeads.length > 0 && (
              <Chip
                icon={<DotIcon sx={{ fontSize: 8, color: 'success.main' }} />}
                label={`${activeLeads.length} lead${activeLeads.length !== 1 ? 's' : ''}`}
                size="small"
                variant="outlined"
                color="success"
                sx={{ height: 22, fontSize: '0.7rem' }}
              />
            )}
          </Box>
          <Button
            size="small"
            endIcon={<ArrowIcon sx={{ fontSize: 14 }} />}
            onClick={() => navigate('/leads')}
            sx={{ fontSize: '0.7rem', py: 0.25 }}
          >
            View All
          </Button>
        </Box>

        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', mb: 1, display: 'block' }}>
          Leads viewing your pitch sites in the last 7 days
        </Typography>

        {loading ? (
          <Box>
            {[...Array(3)].map((_, i) => (
              <Skeleton key={i} variant="rectangular" height={48} sx={{ borderRadius: 0.5, mb: 0.5 }} />
            ))}
          </Box>
        ) : activeLeads.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 3 }}>
            <ViewIcon sx={{ fontSize: 32, color: 'grey.300', mb: 1 }} />
            <Typography variant="caption" color="text.secondary" display="block">
              No lead activity in the last 7 days
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
              Add tracking pixels to your pitch sites to see activity
            </Typography>
          </Box>
        ) : (
          <List disablePadding>
            {activeLeads.map((lead) => (
              <ListItem
                key={lead.leadId}
                sx={{
                  px: 0.75,
                  py: 0.5,
                  borderRadius: 0.5,
                  '&:hover': { bgcolor: 'grey.50' },
                  cursor: 'pointer',
                }}
                onClick={() => navigate(`/leads/${lead.leadId}`)}
              >
                <ListItemAvatar sx={{ minWidth: 36 }}>
                  <Avatar
                    sx={{
                      width: 28,
                      height: 28,
                      bgcolor: 'info.light',
                      color: 'info.dark',
                    }}
                  >
                    <ViewIcon sx={{ fontSize: 16 }} />
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <Typography variant="caption" fontWeight={600} sx={{ fontSize: '0.8rem' }}>
                        {formatInstagramHandle(lead.instagramHandle)}
                      </Typography>
                      <Chip
                        label={`${lead.totalVisits} visit${lead.totalVisits !== 1 ? 's' : ''}`}
                        size="small"
                        color="info"
                        sx={{ height: 18, fontSize: '0.65rem', fontWeight: 600 }}
                      />
                      {lead.uniquePageCount > 1 && (
                        <Chip
                          label={`${lead.uniquePageCount} pages`}
                          size="small"
                          variant="outlined"
                          sx={{ height: 18, fontSize: '0.65rem' }}
                        />
                      )}
                    </Box>
                  }
                  secondary={
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                        {lead.businessName || lead.domains?.[0] || ''}
                        {(lead.lastCity || lead.lastCountry) && (
                          <Typography component="span" variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', ml: 0.75 }}>
                            — {[lead.lastCity, lead.lastCountry].filter(Boolean).join(', ')}
                          </Typography>
                        )}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', whiteSpace: 'nowrap', ml: 1 }}>
                        {formatRelativeDate(lead.lastVisitAt)}
                      </Typography>
                    </Box>
                  }
                />
              </ListItem>
            ))}
          </List>
        )}
      </CardContent>
    </Card>
  );
};

export default ActiveLeadsCard;
